/** The DOM pieces of the manifest editor's form: elements, buttons, fields, combo boxes and chips. */

export type Child = Node | string | undefined | false;

export function h<K extends keyof HTMLElementTagNameMap>(
    tag: K,
    attrs: Record<string, string | boolean | undefined> = {},
    ...children: Child[]
): HTMLElementTagNameMap[K] {
    const element = document.createElement(tag);
    for (const [key, value] of Object.entries(attrs)) {
        if (value === undefined || value === false) continue;
        if (key === "class") element.className = String(value);
        else element.setAttribute(key, value === true ? "" : value);
    }
    for (const child of children) if (child !== undefined && child !== false) element.append(child);
    return element;
}

export function icon(name: string, extra = ""): HTMLElement {
    return h("span", { class: `codicon codicon-${name} ${extra}`, "aria-hidden": "true" });
}

export function iconButton(name: string, title: string, onClick: (event: MouseEvent) => void): HTMLButtonElement {
    const button = h("button", { type: "button", class: "wb-mf-icon-button", title, "aria-label": title }, icon(name));
    button.addEventListener("click", (event) => {
        event.stopPropagation();
        onClick(event);
    });
    return button;
}

export function externalLink(url: string, title: string): HTMLElement {
    const link = h(
        "a",
        { class: "wb-mf-icon-button", href: url, target: "_blank", rel: "noopener", title, "aria-label": title },
        icon("link-external"),
    );
    link.addEventListener("click", (event) => event.stopPropagation());
    return link;
}

export function button(text: string, onClick: () => void, primary = false): HTMLButtonElement {
    const element = h(
        "button",
        { type: "button", class: primary ? "wb-mf-button wb-mf-primary" : "wb-mf-button" },
        text,
    );
    element.addEventListener("click", onClick);
    return element;
}

/** A text field; `input` follows each keystroke, `change` gets the value when the field is left or Enter is pressed. */
export function textInput(
    value: string,
    placeholder: string,
    key: string,
    on: { input?: (value: string) => void; change?: (value: string) => void },
): HTMLInputElement {
    const input = h("input", { type: "text", placeholder, spellcheck: "false", "data-key": key });
    input.value = value;
    if (on.input) input.addEventListener("input", () => on.input!(input.value));
    if (on.change) commitOnLeave(input, value, on.change);
    return input;
}

/**
 * Call `change` when the field is left or Enter is pressed with a value other
 * than the last one given. The comparison is with `value`, so text put back
 * into a field drawn again is written too.
 */
export function commitOnLeave(
    input: HTMLInputElement | HTMLTextAreaElement,
    value: string,
    change: (value: string) => void,
): void {
    // The form puts text typed into such a field back when it draws the field again.
    input.dataset["commit"] = "";
    let committed = value;
    const commit = () => {
        if (input.value === committed) return;
        committed = input.value;
        change(input.value);
    };
    input.addEventListener("blur", commit);
    input.addEventListener("keydown", (event: Event) => {
        if ((event as KeyboardEvent).key === "Enter" && input instanceof HTMLInputElement) commit();
    });
}

/** A labelled row of the form: the title on the left, the control on the right. */
export function field(title: string, control: HTMLElement): HTMLElement {
    return h(
        "div",
        { class: "wb-mf-field" },
        h("div", { class: "wb-mf-field-title" }, title),
        h("div", { class: "wb-mf-field-control" }, control),
    );
}

export interface ComboOption {
    value: string;
    /** What the row shows; the value when absent. */
    content?: Child[];
    disabled?: boolean;
}

export interface ComboSpec {
    value: string;
    placeholder: string;
    key: string;
    /** The options for the text typed since the list opened, "" when nothing was typed; they may come later. */
    options(query: string): ComboOption[] | Promise<ComboOption[]>;
    onInput?(value: string): void;
    onPick(value: string): void;
    /** The value is committed by leaving the field, or by Enter without a list. */
    onChange?(value: string): void;
    /** Open the list on the first focus while the field is empty. */
    openWhenEmpty?: boolean;
    icon?: string;
    mono?: boolean;
    disabled?: boolean;
}

/**
 * A text field with a drop-down list of suggestions, as a combo box: the list
 * opens on a click, a key or the chevron, follows what is typed, and an
 * option is picked with a click or Enter. Anything typed is a value too.
 */
export function combo(spec: ComboSpec): HTMLElement {
    const input = h("input", {
        type: "text",
        placeholder: spec.placeholder,
        spellcheck: "false",
        "data-key": spec.key,
        role: "combobox",
        "aria-expanded": "false",
        autocomplete: "off",
        disabled: spec.disabled,
    });
    input.value = spec.value;
    const list = h("div", { class: "wb-mf-popup", role: "listbox", hidden: true });
    const chevron = h(
        "button",
        {
            type: "button",
            class: "wb-mf-combo-chevron",
            tabindex: "-1",
            "aria-label": "Show suggestions",
            disabled: spec.disabled,
        },
        icon("chevron-down"),
    );
    const root = h(
        "div",
        { class: `wb-mf-combo${spec.mono ? " wb-mf-mono" : ""}${spec.icon ? " wb-mf-combo-icon" : ""}` },
        spec.icon && icon(spec.icon, "wb-mf-combo-lead"),
        input,
        chevron,
        list,
    );
    let typed = false;
    let active = -1;
    let options: ComboOption[] = [];
    let opened = false;
    let drawn = 0;

    const show = (rows: Node[]) => {
        list.replaceChildren(...rows);
        list.querySelector(".wb-mf-active")?.scrollIntoView({ block: "nearest" });
    };
    const rows = () => {
        if (active >= options.length) active = options.length - 1;
        if (options.length === 0) return [h("div", { class: "wb-mf-option wb-mf-disabled" }, "No matches")];
        return options.map((option, index) => {
            const row = h(
                "div",
                {
                    class: `wb-mf-option${index === active ? " wb-mf-active" : ""}${option.disabled ? " wb-mf-disabled" : ""}`,
                    role: "option",
                    "aria-selected": String(option.value === input.value),
                },
                ...(option.content ?? [option.value]),
            );
            row.addEventListener("mousedown", (event) => event.preventDefault());
            row.addEventListener("click", () => pick(option));
            return row;
        });
    };
    const draw = () => {
        const id = ++drawn;
        const result = spec.options(typed ? input.value.trim() : "");
        if (!(result instanceof Promise)) {
            options = result;
            show(rows());
            return;
        }
        if (options.length === 0) show([h("div", { class: "wb-mf-option wb-mf-disabled" }, "Loading…")]);
        result.then(
            (found) => {
                if (id !== drawn || list.hidden) return;
                options = found;
                show(rows());
            },
            (error: unknown) => {
                if (id !== drawn || list.hidden) return;
                options = [];
                show([
                    h(
                        "div",
                        { class: "wb-mf-option wb-mf-disabled" },
                        error instanceof Error ? error.message : String(error),
                    ),
                ]);
            },
        );
    };
    const open = () => {
        if (!list.hidden || spec.disabled) return;
        list.hidden = false;
        input.setAttribute("aria-expanded", "true");
        typed = false;
        active = -1;
        options = [];
        draw();
    };
    const close = () => {
        list.hidden = true;
        input.setAttribute("aria-expanded", "false");
    };
    const pick = (option: ComboOption) => {
        if (option.disabled) return;
        input.value = option.value;
        close();
        spec.onPick(option.value);
    };

    input.addEventListener("focus", () => {
        if (spec.openWhenEmpty && !opened && input.value === "") open();
        opened = true;
    });
    input.addEventListener("click", open);
    input.addEventListener("input", () => {
        open();
        typed = true;
        active = -1;
        draw();
        spec.onInput?.(input.value);
    });
    input.addEventListener("keydown", (event) => {
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            if (list.hidden) open();
            const step = event.key === "ArrowDown" ? 1 : -1;
            active = Math.max(0, Math.min(options.length - 1, active + step));
            show(rows());
        } else if (event.key === "Enter" && !list.hidden && options[active] !== undefined) {
            event.preventDefault();
            pick(options[active]!);
        } else if (event.key === "Escape" && !list.hidden) {
            event.stopPropagation();
            close();
        }
    });
    input.addEventListener("blur", close);
    if (spec.onChange) commitOnLeave(input, spec.value, spec.onChange);
    chevron.addEventListener("mousedown", (event) => event.preventDefault());
    chevron.addEventListener("click", () => {
        if (list.hidden) {
            input.focus();
            open();
        } else close();
    });
    return root;
}

export interface ChipsSpec {
    values: string[];
    placeholder: string;
    key: string;
    /** The values to choose from; without them anything typed is added. */
    options?: string[];
    /** What a field with no value says, such as "All targets". */
    empty?: string;
    onChange(values: string[]): void;
    disabled?: boolean;
}

/**
 * A list of values shown as chips in one field. With `options` it is a
 * multi-select combo box whose list checks the chosen values; without, a
 * value is typed and added with Enter. Backspace in the empty field removes
 * the last chip.
 */
export function chips(spec: ChipsSpec): HTMLElement {
    const values = [...spec.values];
    const input = h("input", {
        type: "text",
        spellcheck: "false",
        "data-key": spec.key,
        autocomplete: "off",
        role: spec.options ? "combobox" : undefined,
        disabled: spec.disabled,
    });
    const list = h("div", { class: "wb-mf-popup", role: "listbox", "aria-multiselectable": "true", hidden: true });
    const box = h("div", { class: "wb-mf-chips-values" });
    const root = h("div", { class: `wb-mf-chips${spec.disabled ? " wb-mf-disabled" : ""}` }, box, list);
    let active = -1;
    let shown: string[] = [];

    let pending = false;
    // While the field has the focus, changes are written when it is left, so the list stays open between picks.
    const changed = () => {
        drawChips();
        drawList();
        if (document.activeElement === input) pending = true;
        else spec.onChange([...values]);
    };
    const drawChips = () => {
        // The chips go before the field, which stays in place: moving it would take its focus.
        box.querySelectorAll(".wb-mf-chip").forEach((chip) => chip.remove());
        if (input.parentElement !== box) box.append(input);
        input.before(
            ...values.map((value) => {
                const chip = h("span", { class: "wb-mf-chip" }, value);
                if (!spec.disabled) {
                    chip.append(
                        iconButton("close", `Remove ${value}`, () => {
                            values.splice(values.indexOf(value), 1);
                            changed();
                        }),
                    );
                }
                return chip;
            }),
        );
        input.placeholder = values.length ? "" : (spec.empty ?? spec.placeholder);
    };
    const drawList = () => {
        if (spec.options === undefined || list.hidden) return;
        const query = input.value.trim().toLowerCase();
        shown = spec.options.filter((option) => option.includes(query));
        if (active >= shown.length) active = shown.length - 1;
        list.replaceChildren(
            ...shown.map((option, index) => {
                const checked = values.includes(option);
                const row = h(
                    "div",
                    {
                        class: `wb-mf-option wb-mf-check-option${index === active ? " wb-mf-active" : ""}`,
                        role: "option",
                        "aria-selected": String(checked),
                    },
                    icon(checked ? "check" : "blank"),
                    option,
                );
                row.addEventListener("mousedown", (event) => event.preventDefault());
                row.addEventListener("click", () => toggle(option));
                return row;
            }),
        );
    };
    const toggle = (value: string) => {
        if (values.includes(value)) values.splice(values.indexOf(value), 1);
        else values.push(value);
        input.value = "";
        changed();
        input.focus();
    };
    const open = () => {
        if (spec.options === undefined || !list.hidden || spec.disabled) return;
        list.hidden = false;
        active = -1;
        drawList();
    };

    if (spec.options !== undefined) {
        const chevron = h(
            "button",
            {
                type: "button",
                class: "wb-mf-combo-chevron",
                tabindex: "-1",
                "aria-label": "Show choices",
                disabled: spec.disabled,
            },
            icon("chevron-down"),
        );
        chevron.addEventListener("mousedown", (event) => event.preventDefault());
        chevron.addEventListener("click", () => {
            if (list.hidden) {
                input.focus();
                open();
            } else list.hidden = true;
        });
        root.insertBefore(chevron, list);
        root.classList.add("wb-mf-chips-combo");
    }
    root.addEventListener("mousedown", (event) => {
        if (event.target === root || event.target === box) {
            event.preventDefault();
            input.focus();
            open();
        }
    });
    input.addEventListener("click", open);
    input.addEventListener("input", () => {
        open();
        active = 0;
        drawList();
    });
    input.addEventListener("blur", () => {
        list.hidden = true;
        // A tag typed and left without Enter is kept, as a combo box's text is.
        const text = input.value.trim();
        input.value = "";
        if (spec.options === undefined && text !== "" && !values.includes(text)) {
            values.push(text);
            drawChips();
            pending = true;
        }
        if (pending) {
            pending = false;
            spec.onChange([...values]);
        }
    });
    input.addEventListener("keydown", (event) => {
        const text = input.value.trim();
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            open();
            active = Math.max(0, Math.min(shown.length - 1, active + (event.key === "ArrowDown" ? 1 : -1)));
            drawList();
        } else if (event.key === "Enter" || (event.key === "," && spec.options === undefined)) {
            event.preventDefault();
            if (spec.options !== undefined) {
                const option = shown[active] ?? (shown.length === 1 ? shown[0] : undefined);
                if (option !== undefined) toggle(option);
            } else if (text !== "" && !values.includes(text)) {
                toggle(text);
            }
        } else if (event.key === "Backspace" && input.value === "" && values.length > 0) {
            values.pop();
            changed();
        } else if (event.key === "Escape" && !list.hidden) {
            event.stopPropagation();
            list.hidden = true;
        }
    });
    drawChips();
    return root;
}
